import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, Trash2, Eye, Download } from 'lucide-react';
import { ROUTES } from '../../lib/constants';
import { formatRelativeTime } from '../../utils/formatters';
import { exportDocumentToPDF } from '../../utils/pdfExport';

const DocumentCard = ({ document, onDelete }) => {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100/60 text-green-700 border-green-200/50';
      case 'processing': return 'bg-blue-100/60 text-blue-700 border-blue-200/50';
      case 'pending': return 'bg-yellow-100/60 text-yellow-700 border-yellow-200/50';
      case 'failed': return 'bg-red-100/60 text-red-700 border-red-200/50';
      default: return 'bg-gray-100/60 text-gray-700 border-gray-200/50';
    }
  };

  const handleView = () => {
    navigate(ROUTES.DOCUMENT.replace(':id', document.id));
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this document?')) {
      onDelete(document.id);
    }
  };

  return (
    <div 
      onClick={handleView}
      className="backdrop-blur-sm bg-white/40 border border-cream-300/50 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 hover:shadow-lg hover:border-brown-300/50 transition-all cursor-pointer group animate-fade-in"
    >
      {/* Header - Responsive */}
      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
          <div className="flex-shrink-0 mt-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-brown-100 to-brown-200 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:from-brown-200 group-hover:to-brown-300 transition-all shadow-sm">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-brown-700" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-brown-900 truncate group-hover:text-brown-700 transition-colors">
              {document.file_name}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1 sm:mt-1.5">
              <span className={`px-2 sm:px-2.5 py-0.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full border ${getStatusColor(document.processing_status)}`}>
                {document.processing_status}
              </span>
              <span className="text-xs sm:text-sm text-brown-500 flex items-center">
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                {/* 3. CHANGE THIS LINE TO USE THE HELPER */}
                {formatRelativeTime(document.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Preview - Responsive text */}
      {document.summary && (
        <p className="text-xs sm:text-sm md:text-base text-brown-600 line-clamp-2 mb-3 sm:mb-4 leading-relaxed">
          {document.summary}
        </p>
      )}

      {/* Keywords - Responsive badges */}
      {document.keywords && document.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {document.keywords.slice(0, 4).map((keyword, index) => (
            <span 
              key={index}
              className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-brown-50/50 text-brown-700 text-xs sm:text-sm rounded-full border border-brown-100"
            >
              {keyword}
            </span>
          ))}
          {document.keywords.length > 4 && (
            <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-cream-50/50 text-brown-600 text-xs sm:text-sm rounded-full border border-cream-200">
              +{document.keywords.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Actions - Responsive layout */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-3 sm:pt-4 border-t border-cream-200/50 gap-2 sm:gap-0">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleView}
            className="flex items-center space-x-1 sm:space-x-1.5 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-brown-700 hover:bg-brown-50/50 rounded-lg transition-all font-medium"
          >
            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>View</span>
          </button>
          {document.processing_status === 'completed' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                exportDocumentToPDF(document);
              }}
              className="flex items-center space-x-1 sm:space-x-1.5 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-brown-600 hover:bg-cream-50/50 rounded-lg transition-all font-medium"
            >
              <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Download</span>
            </button>
          )}
        </div>
        <button
          onClick={handleDelete}
          className="self-start sm:self-auto p-1.5 sm:p-2 text-brown-400 hover:text-red-600 hover:bg-red-50/50 rounded-lg transition-all"
          title="Delete document"
        >
          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  );
};

export default DocumentCard;